import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import { ElementTarget } from '../../types/formulation';

interface ElementTargetTableProps {
  targets: ElementTarget[];
  onChange: (targets: ElementTarget[]) => void;
  elements?: { id: string; symbol: string; name: string; atomicWeight: number }[];
  readOnly?: boolean;
}

const ElementTargetTable: React.FC<ElementTargetTableProps> = ({
  targets = [],
  onChange,
  elements = [],
  readOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Default elements if none provided
  const defaultElements = [
    { id: "1", symbol: "N", name: "Nitrogen", atomicWeight: 14.007 },
    { id: "2", symbol: "P", name: "Phosphorus", atomicWeight: 30.973762 },
    { id: "3", symbol: "K", name: "Potassium", atomicWeight: 39.0983 },
    { id: "4", symbol: "Ca", name: "Calcium", atomicWeight: 40.078 },
    { id: "5", symbol: "Mg", name: "Magnesium", atomicWeight: 24.305 },
    { id: "6", symbol: "S", name: "Sulfur", atomicWeight: 32.065 },
    { id: "7", symbol: "Fe", name: "Iron", atomicWeight: 55.845 },
    { id: "8", symbol: "Mn", name: "Manganese", atomicWeight: 54.938 },
    { id: "9", symbol: "Zn", name: "Zinc", atomicWeight: 65.38 },
    { id: "10", symbol: "B", name: "Boron", atomicWeight: 10.811 },
    { id: "11", symbol: "Cu", name: "Copper", atomicWeight: 63.546 },
    { id: "12", symbol: "Mo", name: "Molybdenum", atomicWeight: 95.94 },
    { id: "13", symbol: "Cl", name: "Chlorine", atomicWeight: 35.453 },
    { id: "14", symbol: "Si", name: "Silicon", atomicWeight: 28.0855 },
  ];

  const availableElements = useMemo(() => {
    return elements.length > 0 ? elements : defaultElements;
  }, [elements]);

  // Get unused elements
  const unusedElements = useMemo(() => {
    return availableElements.filter(
      (e) => !targets.some((t) => t.elementSymbol === e.symbol)
    );
  }, [availableElements, targets]);

  const handleTargetChange = (elementSymbol: string, value: number) => {
    const newTargets = targets.map((target) =>
      target.elementSymbol === elementSymbol
        ? { ...target, targetConcentration: value }
        : target
    );

    onChange(newTargets);
  };

  const handleUnitChange = (elementSymbol: string, unit: string) => {
    const newTargets = targets.map((target) =>
      target.elementSymbol === elementSymbol
        ? { ...target, unit: unit }
        : target
    );

    onChange(newTargets);
  };

  const handleAddElement = (elementSymbol: string) => {
    // Find the element to add
    const element = availableElements.find((e) => e.symbol === elementSymbol);
    if (!element) return;

    const newTarget: ElementTarget = {
      elementSymbol: element.symbol,
      elementName: element.name,
      targetConcentration: 0,
      unit: "ppm",
    };

    const newTargets = [...targets, newTarget];
    onChange(newTargets);

    setAnchorEl(null);
  };

  const handleRemoveElement = (elementSymbol: string) => {
    const newTargets = targets.filter(
      (target) => target.elementSymbol !== elementSymbol
    );
    onChange(newTargets);
  };

  const getRecommendedRange = (elementSymbol: string) => {
    // This would ideally come from an API or database
    // These are example ranges for leafy greens
    const ranges: Record<string, { min: number; max: number }> = {
      N: { min: 150, max: 200 },
      NO3: { min: 150, max: 200 },
      NH4: { min: 10, max: 30 },
      P: { min: 40, max: 60 },
      K: { min: 200, max: 300 },
      Ca: { min: 150, max: 200 },
      Mg: { min: 40, max: 50 },
      S: { min: 50, max: 70 },
      Fe: { min: 2, max: 5 },
      Mn: { min: 0.5, max: 1 },
      Zn: { min: 0.1, max: 0.5 },
      B: { min: 0.3, max: 0.7 },
      Cu: { min: 0.05, max: 0.2 },
      Mo: { min: 0.01, max: 0.05 },
      Si: { min: 0.5, max: 2.0 },
    };
    
    return ranges[elementSymbol] || { min: 0, max: 0 };
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box>      {targets.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No elements added yet.
          </Typography>
          {!readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenMenu}
              sx={{ mt: 2 }}
            >
              Add Element
            </Button>
          )}
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Concentration</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Recommended Range</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targets.map((target) => {
                  const range = getRecommendedRange(target.elementSymbol);
                  return (
                    <TableRow key={target.elementSymbol}>
                      <TableCell>{target.elementName}</TableCell>
                      <TableCell>{target.elementSymbol}</TableCell>                      <TableCell width="20%">
                        {readOnly ? (
                          <Typography variant="body2">
                            {target.targetConcentration}
                          </Typography>
                        ) : (
                          <TextField
                            type="number"
                            value={target.targetConcentration}
                            onChange={(e) =>
                              handleTargetChange(
                                target.elementSymbol,
                                parseFloat(e.target.value) || 0
                              )
                            }
                            size="small"
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        )}
                      </TableCell>                      <TableCell width="15%">
                        {readOnly ? (
                          <Typography variant="body2">
                            {target.unit || "ppm"}
                          </Typography>
                        ) : (
                          <FormControl fullWidth size="small">
                            <Select
                              value={target.unit || "ppm"}
                              onChange={(e) =>
                                handleUnitChange(target.elementSymbol, e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="ppm">ppm</MenuItem>
                              <MenuItem value="mg/L">mg/L</MenuItem>
                              <MenuItem value="mmol/L">mmol/L</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {range.min && range.max ? (
                          <Typography variant="body2">
                            {range.min} - {range.max} ppm
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not specified
                          </Typography>
                        )}
                      </TableCell>                      <TableCell align="center">
                        {!readOnly && (
                          <Tooltip title="Remove element">
                            <IconButton
                              onClick={() => handleRemoveElement(target.elementSymbol)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>          {!readOnly && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenMenu}
                disabled={unusedElements.length === 0}
              >
                Add Element
              </Button>
            </Box>
          )}
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}
      >
        {unusedElements.map((element) => (
          <MenuItem
            key={element.id}
            onClick={() => handleAddElement(element.symbol)}
          >
            {element.name} ({element.symbol})
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ElementTargetTable;
